import type { ReactElement } from 'react';
import type { WorkspaceBillingContract } from '@nest-react/domain';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui';
import { CreditLabel } from '~/components/layout/CreditLabel';

export const BillingOverview = ({
  summary,
  onUpgrade,
  onGetCredits,
}: {
  summary: WorkspaceBillingContract;
  onUpgrade: () => void;
  onGetCredits: () => void;
}): ReactElement => {
  const { t, i18n } = useTranslation('billing');
  const locale = i18n.resolvedLanguage;
  const periodEnd = summary.subscription.currentPeriodEnd
    ? new Date(summary.subscription.currentPeriodEnd).toLocaleDateString(locale)
    : null;
  const paidThrough = summary.subscription.paidThroughAt
    ? new Date(summary.subscription.paidThroughAt).toLocaleDateString(locale)
    : null;
  const breakdown = [
    ['planCredits', summary.creditBreakdown.plan],
    ['carriedCredits', summary.creditBreakdown.carryover],
    ['purchasedCredits', summary.creditBreakdown.purchased],
    ['promotionalCredits', summary.creditBreakdown.promotional],
    ['existingCredits', summary.creditBreakdown.existing],
  ] as const;

  return (
    <div className="grid gap-6">
      <section className="rounded-3xl border border-border bg-elevated shadow-sm">
        <div className="flex flex-col gap-5 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
          <div>
            <p className="text-sm font-medium text-muted">{t('currentPlan')}</p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <h2 className="text-3xl font-semibold text-foreground">
                {summary.subscription.plan}
              </h2>
              <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
                {summary.subscription.status}
              </span>
            </div>
            <p className="mt-2 text-sm text-muted">
              {periodEnd
                ? t('creditRefreshOn', { date: periodEnd })
                : t('noScheduledRefresh')}
            </p>
            {paidThrough ? (
              <p className="mt-1 text-sm text-muted">
                {t('planEndsOn', { date: paidThrough })}
                {summary.subscription.billingCadence === 'YEAR'
                  ? ` · ${t('noAutomaticRenewal')}`
                  : null}
              </p>
            ) : null}
          </div>
          <Button onClick={onUpgrade}>{t('upgradePlan')}</Button>
        </div>
      </section>

      <section className="rounded-3xl border border-border bg-elevated shadow-sm">
        <div className="flex flex-col gap-4 border-b border-border-subtle p-5 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-5">
          <div>
            <p className="text-sm font-medium text-muted">
              {t('workspaceCredits')}
            </p>
            <p className="mt-1.5">
              <CreditLabel
                balance={summary.balance}
                locale={locale ?? 'en'}
                variant="display"
              />
            </p>
          </div>
          <Button variant="secondary" onClick={onGetCredits}>
            {t('getCredits')}
          </Button>
        </div>
        <dl className="divide-y divide-border-subtle px-5 sm:px-6">
          {breakdown.map(([label, value]) => (
            <div
              className="flex items-center justify-between gap-4 py-3"
              key={label}
            >
              <dt className="text-sm text-muted">{t(label)}</dt>
              <dd>
                <CreditLabel
                  balance={value}
                  locale={locale ?? 'en'}
                  maximumFractionDigits={6}
                  showUnit={false}
                  variant="value"
                />
              </dd>
            </div>
          ))}
        </dl>
      </section>
    </div>
  );
};
